import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  FormHelperText,
  LinearProgress,
  ListItemIcon,
  Collapse,
  InputAdornment,
  Checkbox,
  TableSortLabel,
} from "@mui/material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PersonIcon from "@mui/icons-material/Person";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloseIcon from "@mui/icons-material/Close";
import TableRowsIcon from "@mui/icons-material/TableRows";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import ForumIcon from "@mui/icons-material/Forum";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ReactMarkdown from "react-markdown";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SyncIcon from "@mui/icons-material/Sync";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import OutreachStatistics from './OutreachStatistics';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import UserForm from './UserForm';
import StorageIcon from '@mui/icons-material/Storage';
import FileUploadTab from './FileUploadTab';
import ResearchStatistics from './ResearchStatistics';
import LogViewerTab from './LogViewerTab';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import DnsIcon from '@mui/icons-material/Dns';
import DNSStatistics from './DNSStatistics';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import LushaCreditDashboard from './LushaCreditDashboard';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DNSChangeTracker from './DNSChangeTracker';
import TimelineIcon from '@mui/icons-material/Timeline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupervisorAssignments from './SupervisorAssignments';
import TeamManagement from './TeamManagement';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import GroupsIcon from '@mui/icons-material/Groups';
import UploadedFilesList from './UploadedFilesList';
import EmailAdminTab from './EmailAdminTab';
import EmailIcon from '@mui/icons-material/Email';
import logger from '../utils/logger';

const USER_DELETE_IMPACT_LABELS = {
  subscriptions: 'Subscription records',
  payment_history: 'Payment history entries',
  subscription_addons: 'Subscription add-ons',
  chat_threads: 'Chat threads',
  chat_messages: 'Chat messages',
  login_history: 'Login history entries',
  sales_outreaches: 'Sales outreach records',
  lusha_enriched_contacts: 'Lusha enriched contacts',
  lusha_credit_usage: 'Lusha credit usage records',
  lusha_person_credit_usage: 'Lusha person credit records',
  supervisor_assignments: 'Supervisor assignments',
  salesforce_connections: 'Salesforce connections',
  hubspot_connections: 'HubSpot connections',
  claimed_records: 'Claimed company records',
  uploaded_files: 'Uploaded files',
  company_research_logs: 'Company research logs',
};

const USER_RETAINED_ANALYTICS_LABELS = {
  lusha_credit_usage: 'Lusha credit usage records',
  lusha_person_credit_usage: 'Lusha person credit records',
  company_research_logs: 'Company research logs',
};

const ANALYTICS_RETENTION_KEYS = new Set(Object.keys(USER_RETAINED_ANALYTICS_LABELS));

const getUserAuthType = (user) => {
  if (user.login_stats?.last_login_type === 'microsoft') {
    return 'microsoft';
  }
  if (user.login_stats?.last_login_type === 'legacy') {
    return 'legacy';
  }

  const email = user.email?.toLowerCase() || '';
  if (
    email.endsWith('.onmicrosoft.com') ||
    email.includes('outlook.com') ||
    email.includes('hotmail.com') ||
    email.includes('live.com')
  ) {
    return 'microsoft';
  }

  return 'legacy';
};

const sortUsers = (users, sortBy, sortDirection) => {
  const direction = sortDirection === 'asc' ? 1 : -1;

  return [...users].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'username':
        comparison = (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' });
        break;
      case 'display_name':
        comparison = (a.display_name || a.username || '').localeCompare(
          b.display_name || b.username || '',
          undefined,
          { sensitivity: 'base' }
        );
        break;
      case 'email':
        comparison = (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
        break;
      case 'role': {
        const roleRank = (user) => {
          if (user.is_admin) return 0;
          if (user.role_type === 'Supervisor') return 1;
          return 2;
        };
        comparison = roleRank(a) - roleRank(b);
        break;
      }
      case 'last_login': {
        const aTime = a.login_stats?.last_login ? new Date(a.login_stats.last_login).getTime() : 0;
        const bTime = b.login_stats?.last_login ? new Date(b.login_stats.last_login).getTime() : 0;
        comparison = aTime - bTime;
        break;
      }
      case 'disclaimer':
        comparison = Number(Boolean(a.disclaimer_accepted)) - Number(Boolean(b.disclaimer_accepted));
        break;
      case 'auth_type':
        comparison = getUserAuthType(a).localeCompare(getUserAuthType(b));
        break;
      default:
        comparison = 0;
    }

    return comparison * direction;
  });
};

const getSubscriptionStatusRank = (status) => {
  switch (status) {
    case 'active':
      return 0;
    case 'trial':
      return 1;
    case 'cancelled':
      return 2;
    case 'expired':
      return 3;
    default:
      return 4;
  }
};

const getSubscriptionDateValue = (dateString) => {
  if (!dateString) {
    return 0;
  }

  const timestamp = new Date(dateString).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isSubscribedButInactive = (user) => {
  const status = user.subscription?.status;
  return ['active', 'trial'].includes(status) && !user.login_stats?.is_active;
};

const getActivityStatusRank = (status) => {
  switch (status) {
    case 'active':
      return 0;
    case 'inactive':
      return 1;
    case 'never':
      return 2;
    default:
      return 3;
  }
};

const getActivityStatusLabel = (user) => {
  switch (user.login_stats?.activity_status) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'never':
      return 'Never';
    default:
      return 'Unknown';
  }
};

const sortSubscriptionUsers = (users, sortBy, sortDirection) => {
  const direction = sortDirection === 'asc' ? 1 : -1;

  return [...users].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'user':
        comparison = (a.display_name || a.username || '').localeCompare(
          b.display_name || b.username || '',
          undefined,
          { sensitivity: 'base' }
        );
        break;
      case 'status':
        comparison = getSubscriptionStatusRank(a.subscription?.status) -
          getSubscriptionStatusRank(b.subscription?.status);
        break;
      case 'plan':
        comparison = (a.subscription?.plan_name || '').localeCompare(
          b.subscription?.plan_name || '',
          undefined,
          { sensitivity: 'base' }
        );
        break;
      case 'expires':
        comparison = getSubscriptionDateValue(a.subscription?.subscription_end_date) -
          getSubscriptionDateValue(b.subscription?.subscription_end_date);
        break;
      case 'trial_expires':
        comparison = getSubscriptionDateValue(a.subscription?.trial_end_date) -
          getSubscriptionDateValue(b.subscription?.trial_end_date);
        break;
      case 'addons': {
        const aAddonCount = a.addons?.length || 0;
        const bAddonCount = b.addons?.length || 0;
        comparison = aAddonCount - bAddonCount;
        if (comparison === 0 && aAddonCount > 0) {
          const aAddonName = a.addons[0]?.addon?.name || '';
          const bAddonName = b.addons[0]?.addon?.name || '';
          comparison = aAddonName.localeCompare(bAddonName, undefined, { sensitivity: 'base' });
        }
        break;
      }
      case 'last_login':
        comparison = getSubscriptionDateValue(a.login_stats?.last_login) -
          getSubscriptionDateValue(b.login_stats?.last_login);
        break;
      case 'activity':
        comparison = getActivityStatusRank(a.login_stats?.activity_status) -
          getActivityStatusRank(b.login_stats?.activity_status);
        break;
      case 'logins_30d':
        comparison = (a.login_stats?.successful_logins || 0) - (b.login_stats?.successful_logins || 0);
        break;
      default:
        comparison = 0;
    }

    return comparison * direction;
  });
};

const AdminPanel = () => {
  const { 
    isAdmin, 
    isSupervisor,
    getUsers, 
    getClaimStats, 
    createUser, 
    updateUser, 
    deleteUser,
    getUserLoginHistory, 
    getCompanyResearchLogs 
  } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [userActivityFilter, setUserActivityFilter] = useState("all");
  const [userSortBy, setUserSortBy] = useState("username");
  const [userSortDirection, setUserSortDirection] = useState("asc");
  const [claimStats, setClaimStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userClaimedRecords, setUserClaimedRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [unclaimLoading, setUnclaimLoading] = useState(false);
  const [unclaimingRecord, setUnclaimingRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [confirmDeleteRecord, setConfirmDeleteRecord] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [claimsPage, setClaimsPage] = useState(1);
  const [claimsRowsPerPage, setClaimsRowsPerPage] = useState(10);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmBulkUnclaim, setConfirmBulkUnclaim] = useState(false);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Chat history state
  const [chatThreads, setChatThreads] = useState([]);
  const [chatThreadsLoading, setChatThreadsLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadMessagesLoading, setThreadMessagesLoading] = useState(false);
  const [threadDeleteLoading, setThreadDeleteLoading] = useState(false);
  const [confirmDeleteThread, setConfirmDeleteThread] = useState(false);

  // Add state for unclaimed records dialog
  const [unclaimedRecordsDialogOpen, setUnclaimedRecordsDialogOpen] = useState(false);
  const [unclaimedRecords, setUnclaimedRecords] = useState([]);
  const [unclaimedRecordsLoading, setUnclaimedRecordsLoading] = useState(false);

  // Record details dialog state
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRecordDialog, setShowRecordDialog] = useState(false);

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormMode, setUserFormMode] = useState('create'); // 'create' or 'edit'
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    display_name: '',
    email: '',
    is_admin: false,
    role_type: '',
    show_outreach_performance: false
  });
  const [userFormErrors, setUserFormErrors] = useState({});
  const [confirmDeleteUserDialog, setConfirmDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState(null);

  // Add new state for login history
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);

  // Add new state for the active tab in the user details dialog
  const [userDetailTabValue, setUserDetailTabValue] = useState(0);

  // Add state for the audit report dialog
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);

  // Research logs state
  const [researchLogs, setResearchLogs] = useState([]);
  const [researchLogsLoading, setResearchLogsLoading] = useState(false);
  const [researchLogsError, setResearchLogsError] = useState(null);
  const [researchLogsPage, setResearchLogsPage] = useState(1);
  const [researchLogsTotal, setResearchLogsTotal] = useState(0);
  const [researchLogsTotalPages, setResearchLogsTotalPages] = useState(0);
  const [researchLogsFilters, setResearchLogsFilters] = useState({
    username: 'All',
    company: '',
    start_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    end_date: '',
    success: ''
  });

  // Add state for pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Subscription management state
  const [subscriptionUsers, setSubscriptionUsers] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionSettings, setSubscriptionSettings] = useState({});
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState({});

  // Subscription filtering and pagination state
  const [subscriptionSearchQuery, setSubscriptionSearchQuery] = useState("");
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState("all");
  const [subscriptionPlanFilter, setSubscriptionPlanFilter] = useState("all");
  const [subscriptionActivityFilter, setSubscriptionActivityFilter] = useState("all");
  const [showAddonsOnly, setShowAddonsOnly] = useState(false);
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [subscriptionRowsPerPage, setSubscriptionRowsPerPage] = useState(10);
  const [subscriptionSortBy, setSubscriptionSortBy] = useState('user');
  const [subscriptionSortDirection, setSubscriptionSortDirection] = useState('asc');
  const [filteredSubscriptionUsers, setFilteredSubscriptionUsers] = useState([]);

  // Filter and sort users based on search query, activity filter, and sort settings
  useEffect(() => {
    if (!users.length) {
      setFilteredUsers([]);
      return;
    }
    
    let filtered = users;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(query) ||
        (user.display_name && user.display_name.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query))
      );
    }

    if (userActivityFilter === 'active') {
      filtered = filtered.filter(user => user.login_stats?.is_active);
    } else if (userActivityFilter === 'inactive') {
      filtered = filtered.filter(user => !user.login_stats?.is_active);
    }

    filtered = sortUsers(filtered, userSortBy, userSortDirection);
    
    setFilteredUsers(filtered);
  }, [users, searchQuery, userActivityFilter, userSortBy, userSortDirection]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, userActivityFilter, userSortBy, userSortDirection]);

  // Filter subscription users based on search query and filters
  useEffect(() => {
    if (!subscriptionUsers.length) {
      setFilteredSubscriptionUsers([]);
      return;
    }
    
    let filtered = subscriptionUsers;
    
    // Apply search filter
    if (subscriptionSearchQuery.trim()) {
      const query = subscriptionSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(query) ||
        (user.display_name && user.display_name.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query))
      );
    }
    
    // Apply status filter
    if (subscriptionStatusFilter !== "all") {
      filtered = filtered.filter(user => {
        const status = user.subscription?.status || "none";
        return status === subscriptionStatusFilter;
      });
    }
    
    // Apply plan filter
    if (subscriptionPlanFilter !== "all") {
      filtered = filtered.filter(user => {
        const userPlan = user.subscription?.plan_id;
        const userPlanName = user.subscription?.plan_name;
        
        // For plan filtering, check both ID and name matches
        const matchById = userPlan?.toString() === subscriptionPlanFilter;
        const matchByName = userPlanName === subscriptionPlanFilter;
        
        return matchById || matchByName;
      });
    }
    
    // Apply add-ons filter
    if (showAddonsOnly) {
      filtered = filtered.filter(user => {
        return user.addons && user.addons.length > 0;
      });
    }

    if (subscriptionActivityFilter === 'active') {
      filtered = filtered.filter(user => user.login_stats?.is_active);
    } else if (subscriptionActivityFilter === 'inactive') {
      filtered = filtered.filter(user => user.login_stats?.activity_status === 'inactive');
    } else if (subscriptionActivityFilter === 'never') {
      filtered = filtered.filter(user => user.login_stats?.activity_status === 'never');
    } else if (subscriptionActivityFilter === 'subscribed_inactive') {
      filtered = filtered.filter(isSubscribedButInactive);
    }

    filtered = sortSubscriptionUsers(filtered, subscriptionSortBy, subscriptionSortDirection);
    
    setFilteredSubscriptionUsers(filtered);
  }, [
    subscriptionUsers,
    subscriptionSearchQuery,
    subscriptionStatusFilter,
    subscriptionPlanFilter,
    subscriptionActivityFilter,
    showAddonsOnly,
    subscriptionSortBy,
    subscriptionSortDirection
  ]);

  useEffect(() => {
    setSubscriptionPage(1);
  }, [
    subscriptionSearchQuery,
    subscriptionStatusFilter,
    subscriptionPlanFilter,
    subscriptionActivityFilter,
    showAddonsOnly,
    subscriptionSortBy,
    subscriptionSortDirection
  ]);

  // Load subscription data when subscription tab is selected
  useEffect(() => {
    if (tabValue === 6 && isAdmin) {
      fetchSubscriptionData();
    }
  }, [tabValue, isAdmin]);

  // Fetch users list for supervisors (needed for the dropdown in Research History tab)
  useEffect(() => {
    if (isSupervisor && !isAdmin) {
      const fetchUsersForSupervisor = async () => {
        try {
          // We'll use the same endpoint and just extract the usernames
          const response = await api.get('/api/admin/users');
          if (response.data.success) {
            const usersList = response.data.users || [];
            setUsers(usersList);
          }
        } catch (err) {
          logger.error("Error fetching users for supervisor:", err);
        }
      };
      
      fetchUsersForSupervisor();
    }
  }, [isSupervisor, isAdmin]);

  // Reset tabValue if user is not admin and tries to access admin-only tabs
  useEffect(() => {
    if (!isAdmin && !isSupervisor) {
      setTabValue(0);
      return;
    }
    
    // If supervisor tries to access admin-only tabs (0-8), reset to Import tab (9 for supervisor)
    if (isSupervisor && !isAdmin) {
      // Admin-only tabs are 0-8, supervisor tabs start at 9 (Import)
      if (tabValue < 9) {
        // If on an admin-only tab, reset to Import tab (first supervisor tab)
        setTabValue(9);
      }
    }
  }, [isAdmin, isSupervisor, tabValue]);

  // Fetch initial data for the active tab
  useEffect(() => {
    // Change this condition to check for each tab appropriately
    if (!isAdmin && !isSupervisor) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (tabValue === 0 && isAdmin) {
          // Users tab - admin only
          const result = await getUsers();
          if (result.success) {
            const usersList = Array.isArray(result.users) ? result.users : Object.values(result.users || {});
            setUsers(usersList);
          } else {
            setError(result.message);
          }
        } else if (tabValue === 1 && isAdmin) {
          // Claims tab - admin only
          logger.log("Fetching claim stats from tab effect...");
          const result = await getClaimStats();
          logger.log("Claim stats raw response:", result); // Full response logging
          
          if (result.success) {
            logger.log("Setting claim stats:", result.stats);
            // Correctly set the stats directly from the response
            setClaimStats(result.stats);
          } else {
            logger.error("Failed to fetch claim stats:", result.message);
            setError(result.message);
          }
        } else if (tabValue === (isAdmin ? 10 : (isSupervisor ? 10 : 0))) {
          // Research History tab - both admin and supervisor
          await fetchResearchLogs();
          
          // Fetch users for the dropdown
          if (isAdmin) {
            const result = await getUsers();
            if (result.success) {
              const usersList = Object.values(result.users || {});
              setUsers(usersList);
            }
          }
        }
      } catch (err) {
        logger.error("Error loading admin data:", err);
        setError("Failed to load admin data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, isSupervisor, tabValue, getUsers, getClaimStats, getCompanyResearchLogs]);

  const handleTabChange = (event, newValue) => {
    // For supervisors, map visual tab indices to internal tabValue
    // Visual tabs: 0=Import, 1=Research, 2=Outreach, 3=DNS
    // Internal tabValue: 9=Import, 10=Research, 11=Outreach, 12=DNS
    if (isSupervisor && !isAdmin) {
      const supervisorTabMapping = {
        0: 9,  // Import
        1: 10, // Research
        2: 11, // Outreach
        3: 12  // DNS
      };
      setTabValue(supervisorTabMapping[newValue] ?? newValue);
    } else {
      setTabValue(newValue);
    }
  };

  // Fetch a user's claimed records
  const fetchUserClaimedRecords = async (username) => {
    if (!isAdmin || !username) return;

    setRecordsLoading(true);
    setUserClaimedRecords([]);

    try {
      logger.log(`Fetching claims for user: ${username}`);
      const response = await api.get(`/admin/user-claims?username=${username}`);

      if (response.data && response.data.records) {
        logger.log(`Received ${response.data.records.length} claimed records`);
        
        // Add debug logging to inspect the record structure
        if (response.data.records.length > 0) {
          logger.log("Sample record structure:", response.data.records[0]);
          logger.log("Field keys in first record:", Object.keys(response.data.records[0]));
          logger.log("Instellingsnaam value:", response.data.records[0].Instellingsnaam);
          logger.log("Vestigingsadres value:", response.data.records[0].Vestigingsadres);
          logger.log("Vestigingsadres Postcode value:", response.data.records[0]["Vestigingsadres Postcode"]);
          logger.log("Telefoonnummer value:", response.data.records[0].Telefoonnummer);
          logger.log("URL value:", response.data.records[0].URL);
        }
        
        setUserClaimedRecords(response.data.records);
      } else {
        logger.log("No records returned from API");
        setUserClaimedRecords([]);
      }
    } catch (err) {
      logger.error("Error fetching user claims:", err);
      setUserClaimedRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  // Update the fetchLoginHistory function
  const fetchLoginHistory = async (username) => {
    if (!isAdmin || !username) return;

    setLoginHistoryLoading(true);
    setLoginHistory([]);

    try {
      const result = await getUserLoginHistory(username);
      if (result.success) {
        setLoginHistory(result.logins);
      } else {
        setError(result.message);
      }
    } catch (err) {
      logger.error("Error fetching login history:", err);
      setError("Failed to fetch login history");
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  // Modify handleViewUserClaims to NOT fetch login history
  const handleViewUserClaims = (user) => {
    setSelectedUser(user);
    fetchUserClaimedRecords(user.username);
  };

  // Add function to handle opening the audit dialog
  const handleViewUserAudit = (user) => {
    setSelectedUser(user);
    fetchLoginHistory(user.username);
    setAuditDialogOpen(true);
  };

  // Add function to close the audit dialog
  const handleCloseAuditDialog = () => {
    setAuditDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setSelectedUser(null);
    setUserClaimedRecords([]);
    setUserDetailTabValue(0); // Reset tab value when closing dialog
    setSelectedRecords(new Set()); // Clear selections
    setClaimsPage(1); // Reset pagination
  };

  // Handle record selection
  const handleSelectRecord = (recordId) => {
    setSelectedRecords((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  // Handle select all records on current page
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const startIndex = (claimsPage - 1) * claimsRowsPerPage;
      const endIndex = Math.min(startIndex + claimsRowsPerPage, userClaimedRecords.length);
      const pageRecordIds = userClaimedRecords
        .slice(startIndex, endIndex)
        .map((record) => record.record_id);
      setSelectedRecords((prev) => new Set([...prev, ...pageRecordIds]));
    } else {
      const startIndex = (claimsPage - 1) * claimsRowsPerPage;
      const endIndex = Math.min(startIndex + claimsRowsPerPage, userClaimedRecords.length);
      const pageRecordIds = userClaimedRecords
        .slice(startIndex, endIndex)
        .map((record) => record.record_id);
      setSelectedRecords((prev) => {
        const newSet = new Set(prev);
        pageRecordIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  // Check if all records on current page are selected
  const isAllPageSelected = () => {
    const startIndex = (claimsPage - 1) * claimsRowsPerPage;
    const endIndex = Math.min(startIndex + claimsRowsPerPage, userClaimedRecords.length);
    const pageRecords = userClaimedRecords.slice(startIndex, endIndex);
    return pageRecords.length > 0 && pageRecords.every((record) => selectedRecords.has(record.record_id));
  };

  // Check if some records on current page are selected
  const isSomePageSelected = () => {
    const startIndex = (claimsPage - 1) * claimsRowsPerPage;
    const endIndex = Math.min(startIndex + claimsRowsPerPage, userClaimedRecords.length);
    const pageRecords = userClaimedRecords.slice(startIndex, endIndex);
    return pageRecords.some((record) => selectedRecords.has(record.record_id)) && !isAllPageSelected();
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!isAdmin || selectedRecords.size === 0) return;

    setBulkOperationLoading(true);
    setConfirmBulkDelete(false);

    try {
      const recordIds = Array.from(selectedRecords);
      const deletePromises = recordIds.map((recordId) =>
        api.delete("/admin/delete-record", {
          data: { record_id: recordId },
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter((r) => r.status === "fulfilled" && r.value.data.success).length;
      const failed = results.length - successful;

      // Update UI by removing deleted records
      setUserClaimedRecords((prevRecords) =>
        prevRecords.filter((record) => !selectedRecords.has(record.record_id))
      );

      // Clear selections
      setSelectedRecords(new Set());

      // Show success message
      if (failed === 0) {
        setSnackbarMessage(`Successfully deleted ${successful} record(s)`);
      } else {
        setSnackbarMessage(`Deleted ${successful} record(s), ${failed} failed`);
      }
      setSnackbarOpen(true);

      // Refresh claim stats if we're in the claim stats tab
      if (tabValue === 1) {
        await handleRefreshClaimStats();
      }
    } catch (error) {
      logger.error("Error in bulk delete:", error);
      setSnackbarMessage("Error deleting records");
      setSnackbarOpen(true);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  // Handle bulk unclaim
  const handleBulkUnclaim = async () => {
    if (!isAdmin || selectedRecords.size === 0) return;

    setBulkOperationLoading(true);
    setConfirmBulkUnclaim(false);

    try {
      const recordIds = Array.from(selectedRecords);
      const unclaimPromises = recordIds.map((recordId) =>
        api.post("/admin/unclaim-record", {
          record_id: recordId,
        })
      );

      const results = await Promise.allSettled(unclaimPromises);
      const successful = results.filter((r) => r.status === "fulfilled" && r.value.data.success).length;
      const failed = results.length - successful;

      // Update UI by removing unclaimed records
      setUserClaimedRecords((prevRecords) =>
        prevRecords.filter((record) => !selectedRecords.has(record.record_id))
      );

      // Clear selections
      setSelectedRecords(new Set());

      // Show success message
      if (failed === 0) {
        setSnackbarMessage(`Successfully unclaimed ${successful} record(s)`);
      } else {
        setSnackbarMessage(`Unclaimed ${successful} record(s), ${failed} failed`);
      }
      setSnackbarOpen(true);

      // Refresh claim stats if we're in the claim stats tab
      if (tabValue === 1) {
        await handleRefreshClaimStats();
      }
    } catch (error) {
      logger.error("Error in bulk unclaim:", error);
      setSnackbarMessage("Error unclaiming records");
      setSnackbarOpen(true);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  // Add new refresh function to fetch the latest claim statistics
  const handleRefreshClaimStats = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await getClaimStats();
      logger.log("Refreshed claim stats:", result);
      
      if (result.success) {
        setClaimStats(result.stats);
        setSnackbarMessage("Claim statistics refreshed successfully");
        setSnackbarOpen(true);
      } else {
        setError(result.message);
        setSnackbarMessage("Failed to refresh claim statistics");
        setSnackbarOpen(true);
      }
    } catch (err) {
      logger.error("Error refreshing claim stats:", err);
      setError("Failed to refresh claim statistics");
      setSnackbarMessage("Error refreshing claim statistics");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a record
  const handleDeleteRecord = async (recordId) => {
    if (!isAdmin || !recordId) return;

    setDeleteLoading(true);
    setDeletingRecord(recordId);

    try {
      const response = await api.delete("/admin/delete-record", {
        data: { record_id: recordId },
      });

      if (response.data.success) {
        // Update the UI by removing the deleted record
        setUserClaimedRecords((prevRecords) =>
          prevRecords.filter((record) => record.record_id !== recordId)
        );

        // Show success message
        setSnackbarMessage(response.data.message || "Record deleted successfully");
        setSnackbarOpen(true);

        // Refresh claim stats if we're in the claim stats tab
        if (tabValue === 1) {
          await handleRefreshClaimStats();
        }
      } else {
        setSnackbarMessage(response.data.message || "Failed to delete record");
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error("Error deleting record:", error);
      setSnackbarMessage("Error deleting record");
      setSnackbarOpen(true);
    } finally {
      setDeleteLoading(false);
      setDeletingRecord(null);
      setConfirmDeleteRecord(false);
      setRecordToDelete(null);
    }
  };

  const handleDeleteRecordClick = (record) => {
    setRecordToDelete(record);
    setConfirmDeleteRecord(true);
  };

  const handleDeleteRecordCancel = () => {
    setConfirmDeleteRecord(false);
    setRecordToDelete(null);
  };

  const handleDeleteRecordConfirm = () => {
    if (recordToDelete) {
      handleDeleteRecord(recordToDelete.record_id);
    }
  };

  // Update the handleUnclaimRecord function to properly refresh statistics
  const handleUnclaimRecord = async (recordId) => {
    if (!isAdmin || !recordId) return;

    // Set loading state for this operation
    setUnclaimLoading(true);

    try {
      const response = await api.post("/admin/unclaim-record", {
        record_id: recordId,
      });

      if (response.data.success) {
        // Update the UI by removing the unclaimed record
        setUserClaimedRecords((prevRecords) =>
          prevRecords.filter((record) => record.record_id !== recordId)
        );

        // Show success message
        setSnackbarMessage("Record unclaimed successfully");
        setSnackbarOpen(true);

        // Refresh claim stats if we're in the claim stats tab
        if (tabValue === 1) {
          await handleRefreshClaimStats();
        }
      } else {
        setSnackbarMessage(response.data.message || "Failed to unclaim record");
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error("Error unclaiming record:", error);
      setSnackbarMessage("Error unclaiming record");
      setSnackbarOpen(true);
    } finally {
      setUnclaimLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Add a function to handle unclaiming all records for a user
  const handleUnclaimAllRecords = async () => {
    if (!isAdmin || !selectedUser) return;

    setUnclaimLoading(true);

    try {
      // Create an array of promises for each record unclaim operation
      const unclaimPromises = userClaimedRecords.map((record) =>
        api.post("/admin/unclaim-record", { record_id: record.record_id })
      );

      // Execute all unclaim operations in parallel
      const results = await Promise.all(unclaimPromises);

      // Check if all operations were successful
      const allSuccessful = results.every((response) => response.data.success);

      if (allSuccessful) {
        // Clear the displayed records
        setUserClaimedRecords([]);

        // Show success message
        setSnackbarMessage(
          `All records successfully unclaimed from ${
            selectedUser.display_name || selectedUser.username
          }`
        );
        setSnackbarOpen(true);

        // Refresh claim stats if we're in the claim stats tab
        if (tabValue === 1) {
          await handleRefreshClaimStats();
        }
      } else {
        setSnackbarMessage("Some records could not be unclaimed");
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error("Error unclaiming all records:", error);
      setSnackbarMessage("Error unclaiming records");
      setSnackbarOpen(true);
    } finally {
      setUnclaimLoading(false);
    }
  };

  // Handle viewing unclaimed records
  const handleViewUnclaimedRecords = async () => {
    setUnclaimedRecordsDialogOpen(true);
    setUnclaimedRecordsLoading(true);
    
    try {
      const response = await api.get('/admin/unclaimed-records');
      if (response.data.success) {
        setUnclaimedRecords(response.data.records || []);
      } else {
        setSnackbarMessage(response.data.message || 'Failed to fetch unclaimed records');
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error('Error fetching unclaimed records:', error);
      setSnackbarMessage('Error fetching unclaimed records');
      setSnackbarOpen(true);
    } finally {
      setUnclaimedRecordsLoading(false);
    }
  };

  const handleCloseUnclaimedRecordsDialog = () => {
    setUnclaimedRecordsDialogOpen(false);
    setUnclaimedRecords([]);
  };

  // Handle delete thread confirmation
  const handleDeleteThreadConfirm = () => {
    setConfirmDeleteThread(true);
  };

  // Handle delete thread cancel
  const handleDeleteThreadCancel = () => {
    setConfirmDeleteThread(false);
  };

  // Handle delete thread
  const handleDeleteThread = async () => {
    if (!selectedThread) return;

    setThreadDeleteLoading(true);
    try {
      const response = await api.delete(`/api/chats/${selectedThread.id}`);
      if (response.data.success) {
        // Update the thread list
        setChatThreads((prevThreads) =>
          prevThreads.filter((thread) => thread.id !== selectedThread.id)
        );

        // Clear selected thread and messages
        setSelectedThread(null);
        setThreadMessages([]);

        // Show success message
        setSnackbarMessage("Thread deleted successfully");
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error("Error deleting thread:", error);
      setSnackbarMessage("Error deleting thread");
      setSnackbarOpen(true);
    } finally {
      setThreadDeleteLoading(false);
      setConfirmDeleteThread(false);
    }
  };

  // Fetch all chat threads for admin
  const fetchChatThreads = async () => {
    setChatThreadsLoading(true);
    try {
      const response = await api.get("/api/admin/chats");
      if (response.data.success) {
        setChatThreads(response.data.threads);
      }
    } catch (error) {
      logger.error("Error fetching chat threads:", error);
      setError("Failed to fetch chat threads");
    } finally {
      setChatThreadsLoading(false);
    }
  };

  // Load thread messages
  const loadThreadMessages = async (threadId) => {
    setThreadMessagesLoading(true);
    try {
      const response = await api.get(`/api/chats/${threadId}`);
      logger.log("Thread messages response:", response.data);

      if (response.data.success) {
        // Specifically extract the data property from the response
        setSelectedThread(response.data.data.thread);
        setThreadMessages(response.data.data.messages);
      } else {
        logger.error("Failed to load thread messages:", response.data.message);
      }
    } catch (error) {
      logger.error("Error loading thread messages:", error);
      setError("Failed to load thread messages");
    } finally {
      setThreadMessagesLoading(false);
    }
  };

  // Effect to load chat threads when tab changes to chat history
  useEffect(() => {
    if (tabValue === 2 && isAdmin) {
      fetchChatThreads();
    }
  }, [tabValue, isAdmin]);

  // Render the Chat History tab
  const renderChatHistoryTab = () => {
    if (!isAdmin) return null;

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          <ForumIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          AI Chat History
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Left column - Thread list */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ height: "60vh", overflow: "auto" }}>
              <List>
                {chatThreadsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : chatThreads.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No chat history found
                    </Typography>
                  </Box>
                ) : (
                  chatThreads.map((thread) => (
                    <ListItem
                      key={thread.id}
                      button
                      selected={
                        selectedThread && selectedThread.id === thread.id
                      }
                      onClick={() => loadThreadMessages(thread.id)}
                      divider
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          <AccountCircleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {thread.title}
                            {thread.user.is_admin && (
                              <Chip
                                label="Admin"
                                size="small"
                                color="primary"
                                sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              User: {thread.user.username}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {new Date(thread.updated_at).toLocaleString()}
                            </Typography>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: "100%" }}
                            >
                              {thread.preview}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Grid>

          {/* Right column - Thread messages */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={2}
              sx={{ height: "60vh", display: "flex", flexDirection: "column" }}
            >
              {!selectedThread ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body1">
                    Select a conversation to view messages
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: "1px solid #eee",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {selectedThread.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User: {selectedThread.user.username} • Created:{" "}
                        {new Date(selectedThread.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Delete Thread">
                        <IconButton
                          color="error"
                          onClick={handleDeleteThreadConfirm}
                          disabled={threadDeleteLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
                    {threadMessagesLoading ? (
                      <Box
                        sx={{ display: "flex", justifyContent: "center", p: 3 }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : threadMessages.length === 0 ? (
                      <Alert severity="info">
                        No messages found in this thread
                      </Alert>
                    ) : (
                      threadMessages.map((message) => (
                        <Box
                          key={message.id}
                          sx={{
                            mb: 2,
                            display: "flex",
                            flexDirection:
                              message.role === "user" ? "row-reverse" : "row",
                            alignItems: "flex-start",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              mx: 1,
                              bgcolor:
                                message.role === "user"
                                  ? "primary.main"
                                  : "secondary.main",
                            }}
                          >
                            {message.role === "user"
                              ? selectedThread.user.username
                              : message.role === "assistant"
                              ? "AI"
                              : "S"}
                          </Avatar>

                          <Box sx={{ maxWidth: "85%" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                mb: 0.5,
                                textAlign:
                                  message.role === "user" ? "right" : "left",
                              }}
                            >
                              {message.role === "user"
                                ? selectedThread.user.username
                                : "AI Assistant"}{" "}
                              • {new Date(message.timestamp).toLocaleString()}
                            </Typography>

                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor:
                                  message.role === "user"
                                    ? "primary.light"
                                    : message.role === "assistant"
                                    ? "#f0f0f0"
                                    : "#fff3cd",
                                color:
                                  message.role === "user"
                                    ? "primary.contrastText"
                                    : "text.primary",
                              }}
                            >
                              {message.role === "assistant" ? (
                                <ReactMarkdown>{message.content}</ReactMarkdown>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap" }}
                                >
                                  {message.content}
                                </Typography>
                              )}
                            </Paper>
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Confirm Delete Thread Dialog */}
        <Dialog open={confirmDeleteThread} onClose={handleDeleteThreadCancel}>
          <DialogTitle>Delete Thread</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this thread? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteThreadCancel}>Cancel</Button>
            <Button
              onClick={handleDeleteThread}
              color="error"
              disabled={threadDeleteLoading}
              startIcon={
                threadDeleteLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Render dialog for viewing a user's claims
  const renderUserClaimsDialog = () => {
    if (!selectedUser) return null;
    
    return (
      <Dialog
        open={!!selectedUser}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Avatar 
              src={selectedUser.profile_pic} 
              alt={selectedUser.display_name}
              sx={{ mr: 2 }}
            />
            <Typography variant="h6">
              Claims for {selectedUser.display_name || selectedUser.username}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {recordsLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : userClaimedRecords.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              This user has no claimed records.
            </Alert>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1">
                    {userClaimedRecords.length} records claimed
                  </Typography>
                  {selectedRecords.size > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedRecords.size} selected
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedRecords.size > 0 && (
                    <>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<LayersClearIcon />}
                        onClick={() => setConfirmBulkUnclaim(true)}
                        disabled={bulkOperationLoading || unclaimLoading || deleteLoading}
                      >
                        Bulk Unclaim ({selectedRecords.size})
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmBulkDelete(true)}
                        disabled={bulkOperationLoading || unclaimLoading || deleteLoading}
                      >
                        Bulk Delete ({selectedRecords.size})
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<LayersClearIcon />}
                    onClick={handleUnclaimAllRecords}
                    disabled={unclaimLoading || bulkOperationLoading}
                  >
                    {unclaimLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Unclaiming...
                      </>
                    ) : (
                      'Unclaim All'
                    )}
                  </Button>
                </Box>
              </Box>
              
              {/* Pagination Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={claimsRowsPerPage}
                    onChange={(e) => {
                      setClaimsRowsPerPage(e.target.value);
                      setClaimsPage(1);
                    }}
                    label="Rows per page"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((claimsPage - 1) * claimsRowsPerPage + 1, userClaimedRecords.length)}-
                  {Math.min(claimsPage * claimsRowsPerPage, userClaimedRecords.length)} of {userClaimedRecords.length}
                </Typography>
                <Pagination
                  count={Math.ceil(userClaimedRecords.length / claimsRowsPerPage)}
                  page={claimsPage}
                  onChange={(event, newPage) => setClaimsPage(newPage)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={isSomePageSelected()}
                          checked={isAllPageSelected()}
                          onChange={handleSelectAll}
                          disabled={bulkOperationLoading || unclaimLoading || deleteLoading}
                        />
                      </TableCell>
                      <TableCell>Institution</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userClaimedRecords
                      .slice((claimsPage - 1) * claimsRowsPerPage, claimsPage * claimsRowsPerPage)
                      .map((record) => (
                      <TableRow key={record.record_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRecords.has(record.record_id)}
                            onChange={() => handleSelectRecord(record.record_id)}
                            disabled={bulkOperationLoading || unclaimLoading || deleteLoading}
                          />
                        </TableCell>
                        <TableCell>{record.Instellingsnaam}</TableCell>
                        <TableCell>{record.Vestigingsadres}</TableCell>
                        <TableCell>{record.Telefoonnummer}</TableCell>
                        <TableCell>{record.URL}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => handleUnclaimRecord(record.record_id)}
                              disabled={unclaimLoading || deleteLoading || bulkOperationLoading}
                            >
                              Unclaim
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => handleDeleteRecordClick(record)}
                              disabled={unclaimLoading || deleteLoading || bulkOperationLoading}
                              startIcon={deletingRecord === record.record_id && deleteLoading ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon />
                              )}
                            >
                              {deletingRecord === record.record_id && deleteLoading ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render unclaimed records dialog
  const renderUnclaimedRecordsDialog = () => {
    return (
      <Dialog
        open={unclaimedRecordsDialogOpen}
        onClose={handleCloseUnclaimedRecordsDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Unclaimed Records ({unclaimedRecords.length})
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {unclaimedRecordsLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : unclaimedRecords.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No unclaimed records found.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Institution</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unclaimedRecords.map((record) => (
                    <TableRow key={record.record_id}>
                      <TableCell>{record.Instellingsnaam || '-'}</TableCell>
                      <TableCell>{record.Vestigingsadres || '-'}</TableCell>
                      <TableCell>{record['Vestigingsadres Plaats'] || record.Vestigingsadres_Plaats || '-'}</TableCell>
                      <TableCell>{record.Telefoonnummer || '-'}</TableCell>
                      <TableCell>
                        {record.URL ? (
                          <a href={record.URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {record.URL}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            setSelectedRecord(record);
                            setShowRecordDialog(true);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnclaimedRecordsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Add a new login history dialog
  const renderUserAuditDialog = () => {
    if (!selectedUser) return null;
    
    return (
      <Dialog
        open={auditDialogOpen}
        onClose={handleCloseAuditDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Avatar 
              src={selectedUser.profile_pic} 
              alt={selectedUser.display_name}
              sx={{ mr: 2 }}
            />
            <Typography variant="h6">
              Login History for {selectedUser.display_name || selectedUser.username}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {loginHistoryLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : loginHistory.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No login history available.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Login Type</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>User Agent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginHistory.map((login) => (
                    <TableRow key={login.id}>
                      <TableCell>
                        {new Date(login.login_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={login.success ? "Success" : "Failed"}
                          color={login.success ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {login.login_type ? (
                          login.login_type === 'microsoft' ? (
                            <Chip
                              label="Microsoft"
                              color="info"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Legacy"
                              color="default"
                              size="small"
                            />
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unknown
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{login.ip_address}</TableCell>
                      <TableCell>{login.user_agent}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAuditDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Record details dialog
  const renderRecordDetailsDialog = () => {
    return (
      <Dialog
        open={showRecordDialog}
        onClose={() => setShowRecordDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">
              Record Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(selectedRecord).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell component="th" scope="row">
                        <strong>{key}</strong>
                      </TableCell>
                      <TableCell>{String(value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No record data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRecordDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Handle opening the user form dialog for creating a new user
  const handleAddUser = () => {
    setUserFormData({
      username: '',
      password: '',
      display_name: '',
      email: '',
      is_admin: false,
      role_type: '',
      show_outreach_performance: false
    });
    setUserFormErrors({});
    setUserFormMode('create');
    setUserDialogOpen(true);
  };

  // Handle opening the user form dialog for editing an existing user
  const handleEditUser = (user) => {
    setUserFormData({
      username: user.username,
      password: '', // Don't populate password field for security
      display_name: user.display_name || '',
      email: user.email || '',
      is_admin: user.is_admin || false,
      role_type: user.role_type || '',
      show_outreach_performance: user.show_outreach_performance || false
    });
    setUserFormErrors({});
    setUserFormMode('edit');
    setUserDialogOpen(true);
  };

  // Handle changes to the user form fields
  const handleUserFormChange = (e) => {
    const { name, type, checked, value } = e.target;
    
    // Special handling for checkboxes
    if (type === 'checkbox') {
      logger.log(`Checkbox ${name} changed to: ${checked}`);
    
    setUserFormData(prev => ({
      ...prev,
        [name]: Boolean(checked)
      }));
    } else {
      setUserFormData(prev => ({
        ...prev,
        [name]: value
    }));
    }
    
    // Clear error for this field if it exists
    if (userFormErrors[name]) {
      setUserFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle submission of the user form
  const handleUserFormSubmit = async () => {
    // Validate form
    const errors = {};
    
    if (userFormMode === 'create') {
      if (!userFormData.username) errors.username = 'Username is required';
      if (!userFormData.password) errors.password = 'Password is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setUserFormErrors(errors);
      return;
    }
    
    try {
      setLoading(true);
      
      let result;
      if (userFormMode === 'create') {
        result = await createUser(userFormData);
      } else {
        // For edit mode, only include fields that have values
        const updateData = {};
        if (userFormData.display_name) updateData.display_name = userFormData.display_name;
        if (userFormData.email) updateData.email = userFormData.email;
        if (userFormData.password) updateData.password = userFormData.password;
        
        // Always include is_admin flag when updating
        updateData.is_admin = !!userFormData.is_admin;
        
        // Always include role_type when updating (may be empty string which is valid)
        updateData.role_type = userFormData.role_type;
        
        // Always include show_outreach_performance flag when updating
        updateData.show_outreach_performance = !!userFormData.show_outreach_performance;
        
        logger.log('Updating user with is_admin:', updateData.is_admin, 'role_type:', updateData.role_type);
        
        result = await updateUser(userFormData.username, updateData);
      }
      
      if (result.success) {
        setSnackbarMessage(result.message);
        setSnackbarOpen(true);
        setUserDialogOpen(false);
        
        // Refresh user list
        fetchUsers();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Error processing user: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Handle user deletion
  const handleDeleteUserClick = (user) => {
    setUserToDelete(user);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setConfirmDeleteUserDialog(true);
  };

  const handleCloseDeleteUserDialog = () => {
    setConfirmDeleteUserDialog(false);
    setUserToDelete(null);
    setDeletePreview(null);
    setDeletePreviewError(null);
  };

  useEffect(() => {
    if (!confirmDeleteUserDialog || !userToDelete) {
      return undefined;
    }

    let cancelled = false;

    const fetchDeletePreview = async () => {
      setDeletePreviewLoading(true);
      setDeletePreviewError(null);

      try {
        const response = await api.get(`/api/admin/users/${userToDelete.username}/delete-preview`);
        if (cancelled) {
          return;
        }

        setDeletePreview(response.data.preview);
      } catch (err) {
        if (cancelled) {
          return;
        }

        setDeletePreviewError(
          err.response?.data?.message || 'Failed to load delete preview'
        );
      } finally {
        if (!cancelled) {
          setDeletePreviewLoading(false);
        }
      }
    };

    fetchDeletePreview();

    return () => {
      cancelled = true;
    };
  }, [confirmDeleteUserDialog, userToDelete]);

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      const result = await deleteUser(userToDelete.username);
      
      if (result.success) {
        setSnackbarMessage(result.message);
        setSnackbarOpen(true);
        handleCloseDeleteUserDialog();
        
        // Refresh user list
        fetchUsers();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Error deleting user: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const result = await getUsers();
      if (result.success) {
        const usersList = Array.isArray(result.users) ? result.users : Object.values(result.users || {});
        setUsers(usersList);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch users: ' + (err.message || 'Unknown error'));
      logger.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch company research logs
  const fetchResearchLogs = async (page = 1, filters = {}) => {
    if (!isAdmin && !isSupervisor) return;
    
    setResearchLogsLoading(true);
    setResearchLogsError(null);
    
    try {
      // Create a copy of the filters without modifying the original
      const apiFilters = {...researchLogsFilters, ...filters};
      
      // If username is 'All', remove it from API request
      if (apiFilters.username === 'All') {
        apiFilters.username = '';
      }
      
      // If status is empty string (All), don't send it to the API
      if (apiFilters.success === '') {
        delete apiFilters.success;
      }
      
      const result = await getCompanyResearchLogs({
        ...apiFilters,
        page,
        per_page: 50
      });
      
      if (result.success) {
        setResearchLogs(result.logs);
        setResearchLogsTotal(result.total);
        setResearchLogsTotalPages(result.pages);
        setResearchLogsPage(result.current_page);
      } else {
        setResearchLogsError(result.message);
      }
    } catch (err) {
      setResearchLogsError('Failed to fetch research logs');
      logger.error('Error fetching research logs:', err);
    } finally {
      setResearchLogsLoading(false);
    }
  };
  
  // Handle research logs filter change
  const handleResearchLogsFilterChange = (e) => {
    const { name, value } = e.target;
    setResearchLogsFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle research logs filter submit
  const handleResearchLogsFilterSubmit = (e) => {
    e.preventDefault();
    fetchResearchLogs(1);
  };
  
  // Handle research logs page change
  const handleResearchLogsPageChange = (e, page) => {
    setResearchLogsPage(page);
    fetchResearchLogs(page);
  };

  // New handler for search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleUserSort = (column) => {
    if (userSortBy === column) {
      setUserSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortBy(column);
      setUserSortDirection('asc');
    }
  };

  const renderUserSortLabel = (column, label, align = 'left') => {
    const sortLabel = (
      <TableSortLabel
        active={userSortBy === column}
        direction={userSortBy === column ? userSortDirection : 'asc'}
        onClick={() => handleUserSort(column)}
      >
        {label}
      </TableSortLabel>
    );

    if (align === 'center') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {sortLabel}
        </Box>
      );
    }

    return sortLabel;
  };

  // Add page change handler
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // Subscription management functions
  const fetchSubscriptionData = async () => {
    setSubscriptionLoading(true);
    try {
      const [usersResponse, statsResponse, settingsResponse, plansResponse] = await Promise.all([
        api.get('/api/admin/subscription/users'),
        api.get('/api/admin/subscription/statistics'),
        api.get('/api/admin/subscription/settings'),
        api.get('/api/admin/subscription/plans')
      ]);

      if (usersResponse.data.success) {
        setSubscriptionUsers(usersResponse.data.data.users);
      }
      if (statsResponse.data.success) {
        setSubscriptionStats(statsResponse.data.data);
      }
      if (settingsResponse.data.success) {
        setSubscriptionSettings(settingsResponse.data.data);
      }
      if (plansResponse.data.success) {
        setAvailablePlans(plansResponse.data.data.plans);
      }
    } catch (error) {
      logger.error('Error fetching subscription data:', error);
      setError('Failed to load subscription data');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleExtendTrial = async (userId, additionalDays = subscriptionSettings.trial_period_days || 14) => {
    // Note: This actually resets the trial from today's date, not extends it
    setSubscriptionActionLoading({ ...subscriptionActionLoading, [`extend_${userId}`]: true });
    try {
      const response = await api.post('/api/admin/subscription/extend-trial', {
        user_id: userId,
        additional_days: additionalDays
      });

      if (response.data.success) {
        setSnackbarMessage(response.data.message || `Trial reset to ${additionalDays} days from today`);
        setSnackbarOpen(true);
        fetchSubscriptionData(); // Refresh data
      }
    } catch (error) {
      logger.error('Error extending trial:', error);
      setError('Failed to extend trial');
    } finally {
      setSubscriptionActionLoading({ ...subscriptionActionLoading, [`extend_${userId}`]: false });
    }
  };

  const handleActivateSubscription = async (userId, planId) => {
    setSubscriptionActionLoading({ ...subscriptionActionLoading, [`activate_${userId}`]: true });
    try {
      const response = await api.post('/api/admin/subscription/activate-user', {
        user_id: userId,
        plan_id: planId
      });

      if (response.data.success) {
        setSnackbarMessage('Subscription activated successfully');
        setSnackbarOpen(true);
        fetchSubscriptionData(); // Refresh data
      }
    } catch (error) {
      logger.error('Error activating subscription:', error);
      setError('Failed to activate subscription');
    } finally {
      setSubscriptionActionLoading({ ...subscriptionActionLoading, [`activate_${userId}`]: false });
    }
  };

  const handleCancelSubscription = async (userId, reason = 'Admin cancelled') => {
    setSubscriptionActionLoading({ ...subscriptionActionLoading, [`cancel_${userId}`]: true });
    try {
      const response = await api.post('/api/admin/subscription/cancel-user', {
        user_id: userId,
        reason: reason
      });

      if (response.data.success) {
        setSnackbarMessage('Subscription cancelled successfully');
        setSnackbarOpen(true);
        fetchSubscriptionData(); // Refresh data
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      setError('Failed to cancel subscription');
    } finally {
      setSubscriptionActionLoading({ ...subscriptionActionLoading, [`cancel_${userId}`]: false });
    }
  };

  const handleChangePlan = async (userId, newPlanId) => {
    setSubscriptionActionLoading({ ...subscriptionActionLoading, [`change_${userId}`]: true });
    try {
      const response = await api.post('/api/admin/subscription/change-plan', {
        user_id: userId,
        plan_id: newPlanId
      });

      if (response.data.success) {
        setSnackbarMessage('Subscription plan changed successfully');
        setSnackbarOpen(true);
        fetchSubscriptionData(); // Refresh data
      }
    } catch (error) {
      logger.error('Error changing subscription plan:', error);
      setError('Failed to change subscription plan');
    } finally {
      setSubscriptionActionLoading({ ...subscriptionActionLoading, [`change_${userId}`]: false });
    }
  };

  const formatSubscriptionDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '-';
    }
  };

  const handleSubscriptionSort = (column) => {
    if (subscriptionSortBy === column) {
      setSubscriptionSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSubscriptionSortBy(column);
      setSubscriptionSortDirection('asc');
    }
  };

  const renderSubscriptionSortLabel = (column, label, align = 'left') => {
    const sortLabel = (
      <TableSortLabel
        active={subscriptionSortBy === column}
        direction={subscriptionSortBy === column ? subscriptionSortDirection : 'asc'}
        onClick={() => handleSubscriptionSort(column)}
      >
        {label}
      </TableSortLabel>
    );

    if (align === 'center') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {sortLabel}
        </Box>
      );
    }

    return sortLabel;
  };

  const updateSubscriptionSettings = async (newSettings) => {
    try {
      const response = await api.put('/api/admin/subscription/settings', newSettings);
      
      if (response.data.success) {
        setSubscriptionSettings(response.data.data);
        setSnackbarMessage('Settings updated successfully');
        setSnackbarOpen(true);
      }
    } catch (error) {
      logger.error('Error updating subscription settings:', error);
      setError('Failed to update settings');
    }
  };

  // If not admin or supervisor, show access denied
  if (!isAdmin && !isSupervisor) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Alert severity="error">
          Access Denied. This panel is only available for administrators and supervisors.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: 2,
          mb: 3, 
          backgroundColor: "#f5f5f5",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%"
        }}
      >
        {/* Content - always visible */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          flexGrow: 1,
          overflowY: "auto" 
        }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: "divider", 
            position: "sticky", 
            top: 0,
            backgroundColor: "#f5f5f5", 
            zIndex: 1200, // High z-index to ensure it stays on top
            overflow: "hidden", // Prevent scroll bars from showing on the container
            flexShrink: 0, // Prevent the navbar from shrinking
            minHeight: { xs: '64px', sm: '72px' } // Ensure minimum height
          }}>
            <Tabs 
              value={isSupervisor && !isAdmin ? (() => {
                // Map internal tabValue back to visual tab index for supervisors
                const reverseMapping = {
                  9: 0,   // Import
                  10: 1,  // Research
                  11: 2,  // Outreach
                  12: 3   // DNS
                };
                return reverseMapping[tabValue] ?? tabValue;
              })() : tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTabs-flexContainer': {
                  gap: { xs: 0, sm: 1 }, // No gap on mobile, small gap on larger screens
                },
                '& .MuiTab-root': {
                  minWidth: { xs: 60, sm: 100 }, // Smaller minimum width on mobile
                  padding: { xs: '6px 8px', sm: '12px 16px' }, // Smaller padding on mobile
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller font on mobile
                  '& .MuiTab-iconWrapper': {
                    marginBottom: { xs: '2px', sm: '4px' }, // Smaller margin on mobile
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '18px', sm: '24px' }, // Smaller icons on mobile
                  },
                },
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              {isAdmin && (
                <Tab 
                  icon={<SupervisorAccountIcon />} 
                  label="Users"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<BookmarkIcon />} 
                  label="Claims"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<SmartToyIcon />} 
                  label="Chat"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<StorageIcon />} 
                  label="Database"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<TextSnippetIcon />} 
                  label="Logs"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<ContactPhoneIcon />} 
                  label="Contact Enrichment"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<CreditCardIcon />} 
                  label="Subs"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<AssignmentIndIcon />} 
                  label="Supervisors"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<GroupsIcon />} 
                  label="Teams"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isAdmin && (
                <Tab 
                  icon={<EmailIcon />} 
                  label="Email"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              {isSupervisor && !isAdmin && (
                <Tab 
                  icon={<StorageIcon />} 
                  label="Import"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
              <Tab 
                icon={<BusinessIcon />} 
                label="Research"
                sx={{ 
                  '& .MuiTab-wrapper': { 
                    flexDirection: { xs: 'column', sm: 'column' },
                    alignItems: 'center'
                  }
                }}
              />
              <Tab 
                icon={<PhoneCallbackIcon />} 
                label="Outreach"
                sx={{ 
                  '& .MuiTab-wrapper': { 
                    flexDirection: { xs: 'column', sm: 'column' },
                    alignItems: 'center'
                  }
                }}
              />
              <Tab 
                icon={<DnsIcon />} 
                label="DNS"
                sx={{ 
                  '& .MuiTab-wrapper': { 
                    flexDirection: { xs: 'column', sm: 'column' },
                    alignItems: 'center'
                  }
                }}
              />
              {isAdmin && (
                <Tab 
                  icon={<TimelineIcon />} 
                  label="Tracker"
                  sx={{ 
                    '& .MuiTab-wrapper': { 
                      flexDirection: { xs: 'column', sm: 'column' },
                      alignItems: 'center'
                    }
                  }}
                />
              )}
            </Tabs>
          </Box>

          {/* Content area */}
          <Box sx={{ 
            flexGrow: 1,
            overflow: 'auto',
            pt: 3 // Add top padding for spacing below sticky navigation bar
          }}>

          {/* Users Panel - Admin Only */}
          {tabValue === 0 && isAdmin && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Manage Users</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                >
                  Add User
                </Button>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {/* Search and filters */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: 1, minWidth: 280 }}
                  placeholder="Search by username, display name, or email"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleClearSearch}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  variant="outlined"
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="user-activity-filter-label">Activity</InputLabel>
                  <Select
                    labelId="user-activity-filter-label"
                    value={userActivityFilter}
                    label="Activity"
                    onChange={(e) => setUserActivityFilter(e.target.value)}
                  >
                    <MenuItem value="all">All users</MenuItem>
                    <MenuItem value="active">Active (last 30 days)</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {/* Show filtered user count */}
              {(searchQuery || userActivityFilter !== 'all') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
                    {searchQuery ? ` matching "${searchQuery}"` : ''}
                    {userActivityFilter === 'active' ? ' (active in last 30 days)' : ''}
                    {userActivityFilter === 'inactive' ? ' (inactive)' : ''}
                  </Typography>
                </Box>
              )}
              
              <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Profile</TableCell>
                      <TableCell sortDirection={userSortBy === 'username' ? userSortDirection : false}>
                        {renderUserSortLabel('username', 'Username')}
                      </TableCell>
                      <TableCell sortDirection={userSortBy === 'display_name' ? userSortDirection : false}>
                        {renderUserSortLabel('display_name', 'Display Name')}
                      </TableCell>
                      <TableCell sortDirection={userSortBy === 'email' ? userSortDirection : false}>
                        {renderUserSortLabel('email', 'Email')}
                      </TableCell>
                      <TableCell align="center" sortDirection={userSortBy === 'role' ? userSortDirection : false}>
                        {renderUserSortLabel('role', 'Role', 'center')}
                      </TableCell>
                      <TableCell align="center" sortDirection={userSortBy === 'last_login' ? userSortDirection : false}>
                        {renderUserSortLabel('last_login', 'Last Login', 'center')}
                      </TableCell>
                      <TableCell align="center" sortDirection={userSortBy === 'disclaimer' ? userSortDirection : false}>
                        {renderUserSortLabel('disclaimer', 'Disclaimer', 'center')}
                      </TableCell>
                      <TableCell align="center" sortDirection={userSortBy === 'auth_type' ? userSortDirection : false}>
                        {renderUserSortLabel('auth_type', 'Auth Type', 'center')}
                      </TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <CircularProgress size={24} sx={{ my: 2 }} />
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          {searchQuery ? 'No users found matching your search' : 'No users found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      getCurrentPageItems().map((user) => (
                        <TableRow key={user.username} hover>
                          <TableCell>
                            <Avatar src={user.profile_pic} alt={user.display_name}>
                              {user.display_name?.charAt(0) || user.username?.charAt(0)}
                            </Avatar>
                          </TableCell>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.display_name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell align="center">
                            {user.is_admin ? (
                              <Chip
                                label="Administrator"
                                color="error"
                                size="small"
                              />
                            ) : user.role_type === 'Supervisor' ? (
                              <Chip
                                label="Supervisor"
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="User"
                                color="primary"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.login_stats?.last_login ? (
                              <Tooltip
                                title={`${new Date(user.login_stats.last_login).toLocaleString()}${user.login_stats.is_active ? '' : ' (inactive)'}`}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">
                                    {new Date(user.login_stats.last_login).toLocaleDateString()}
                                  </Typography>
                                  {!user.login_stats.is_active && (
                                    <Chip label="Inactive" size="small" color="default" variant="outlined" />
                                  )}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Tooltip title="No successful login recorded">
                                <Typography variant="body2" color="text.secondary">Never</Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.disclaimer_accepted ? (
                              <Chip
                                label="Accepted"
                                color="success"
                                size="small"
                                icon={<CheckCircleIcon fontSize="small" />}
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                color="warning"
                                size="small"
                                icon={<WarningIcon fontSize="small" />}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {user.email && user.email.includes('@') ? (
                              getUserAuthType(user) === 'microsoft' ? (
                                <Chip
                                  label="Microsoft"
                                  color="info"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  label="Legacy"
                                  color="default"
                                  size="small"
                                />
                              )
                            ) : null}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <Tooltip title="Edit User">
                                <IconButton 
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditUser(user)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Delete User">
                                <IconButton 
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteUserClick(user)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewUserClaims(user)}
                              >
                                View Claims
                              </Button>
                              
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleViewUserAudit(user)}
                              >
                                Audit
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Add pagination controls */}
              {filteredUsers.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                  <Pagination
                    count={Math.ceil(filteredUsers.length / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Claim Stats Panel - Admin Only */}
          {tabValue === 1 && isAdmin && (
            <Box sx={{ p: 3 }}>
              {loading ? (
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (
                <>
                  {/* Summary Stats Cards */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "primary.light", color: "primary.contrastText" }}>
                        <CardContent>
                          <Typography variant="h5" gutterBottom>
                            {claimStats?.total_records || 0}
                          </Typography>
                          <Typography variant="body2">Total Records</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "success.light", color: "success.contrastText" }}>
                        <CardContent>
                          <Typography variant="h5" gutterBottom>
                            {claimStats?.total_claimed || 0}
                          </Typography>
                          <Typography variant="body2">Total Claims</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card 
                        sx={{ 
                          bgcolor: "warning.light", 
                          color: "warning.contrastText",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 4,
                            transform: "translateY(-2px)",
                            transition: "all 0.2s ease-in-out",
                          }
                        }}
                        onClick={handleViewUnclaimedRecords}
                      >
                        <CardContent>
                          <Typography variant="h5" gutterBottom>
                            {(claimStats?.total_records || 0) - (claimStats?.total_claimed || 0)}
                          </Typography>
                          <Typography variant="body2">Unclaimed Records</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "info.light", color: "info.contrastText" }}>
                        <CardContent>
                          <Typography variant="h5" gutterBottom>
                            {claimStats?.percent_claimed}%
                          </Typography>
                          <Typography variant="body2">Records Claimed</Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={claimStats?.percent_claimed || 0} 
                            sx={{ mt: 1, height: 8, borderRadius: 2 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<SyncIcon />}
                      onClick={handleRefreshClaimStats}
                      disabled={loading}
                    >
                      {loading ? "Refreshing..." : "Refresh Stats"}
                    </Button>
                  </Box>

                  {/* Claims by User */}
                  <Typography variant="h6" gutterBottom>
                    Claims by User
                  </Typography>

                  {Object.keys(claimStats?.users || {}).length === 0 ? (
                    <Alert severity="info">No claims have been made yet.</Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {Object.entries(claimStats?.users || {}).map(
                        ([username, data]) => (
                          <Grid item xs={12} sm={6} md={4} key={username}>
                            <Card
                              variant="outlined"
                              sx={{
                                cursor: "pointer",
                                "&:hover": {
                                  boxShadow: 3,
                                  transition: "box-shadow 0.3s ease-in-out",
                                },
                              }}
                              onClick={() =>
                                handleViewUserClaims({
                                  username,
                                  display_name: data.display_name,
                                  profile_pic: data.profile_pic,
                                })
                              }
                            >
                              <CardContent>
                                <Box
                                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                                >
                                  <Avatar
                                    src={data.profile_pic}
                                    alt={data.display_name}
                                    sx={{ mr: 2 }}
                                  >
                                    {data.display_name?.charAt(0) || username?.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle1">
                                      {data.display_name || username}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      @{username}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography variant="h6">
                                    {data.count} {data.count === 1 ? "record" : "records"} claimed
                                  </Typography>
                                  {data.invalid_claims > 0 && (
                                    <Chip 
                                      label={`${data.invalid_claims} invalid`} 
                                      size="small" 
                                      color="error" 
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      )}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Chat History Panel - Admin Only */}
          {tabValue === 2 && isAdmin && renderChatHistoryTab()}

          {/* File Upload Panel - Admin (Database tab) and Supervisor (Import tab) */}
          {((isAdmin && tabValue === 3) || (isSupervisor && !isAdmin && tabValue === 9)) && (
            <Box>
              <FileUploadTab />
              <UploadedFilesList showAllFiles={isAdmin} />
            </Box>
          )}

          {/* Logs Panel - Admin Only */}
          {tabValue === 4 && isAdmin && <LogViewerTab />}

          {/* Lusha Credits Panel - Admin Only */}
          {tabValue === 5 && isAdmin && <LushaCreditDashboard />}

          {/* Subscription Management Panel - Admin Only */}
          {tabValue === 6 && isAdmin && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>Subscription Management</Typography>
              
              {subscriptionLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Subscription Statistics */}
                  {subscriptionStats && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="primary">
                              {subscriptionStats.total_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Users
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="info.main">
                              {subscriptionStats.trial_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Trial Users
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="success.main">
                              {subscriptionStats.active_subscriptions}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Active Subscriptions
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="error.main">
                              {subscriptionStats.expired_trials}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Expired Trials
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="warning.main">
                              {subscriptionUsers.filter(isSubscribedButInactive).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Subscribed but Inactive
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}

                  {/* Subscription Settings */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>System Settings</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={subscriptionSettings.subscription_enabled || false}
                                onChange={(e) => updateSubscriptionSettings({
                                  ...subscriptionSettings,
                                  subscription_enabled: e.target.checked
                                })}
                              />
                            }
                            label="Subscriptions Enabled"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Trial Period (days)"
                            type="number"
                            value={subscriptionSettings.trial_period_days || 7}
                            onChange={(e) => updateSubscriptionSettings({
                              ...subscriptionSettings,
                              trial_period_days: parseInt(e.target.value)
                            })}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="VAT Rate (%)"
                            type="number"
                            value={subscriptionSettings.vat_rate_percent || 21}
                            onChange={(e) => updateSubscriptionSettings({
                              ...subscriptionSettings,
                              vat_rate_percent: parseFloat(e.target.value)
                            })}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Users with Subscription Status */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>User Subscriptions</Typography>
                      
                      {/* Filter Controls */}
                      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                          label="Search users..."
                          size="small"
                          value={subscriptionSearchQuery}
                          onChange={(e) => setSubscriptionSearchQuery(e.target.value)}
                          sx={{ minWidth: 200 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                        
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={subscriptionStatusFilter}
                            onChange={(e) => setSubscriptionStatusFilter(e.target.value)}
                            label="Status"
                          >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="trial">Trial</MenuItem>
                            <MenuItem value="expired">Expired</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="no_subscription">No Subscription</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Plan</InputLabel>
                          <Select
                            value={subscriptionPlanFilter}
                            onChange={(e) => setSubscriptionPlanFilter(e.target.value)}
                            label="Plan"
                          >
                            <MenuItem value="all">All Plans</MenuItem>
                            {availablePlans.map((plan) => (
                              <MenuItem key={plan.id} value={plan.id.toString()}>
                                {plan.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>App Activity</InputLabel>
                          <Select
                            value={subscriptionActivityFilter}
                            onChange={(e) => setSubscriptionActivityFilter(e.target.value)}
                            label="App Activity"
                          >
                            <MenuItem value="all">All Activity</MenuItem>
                            <MenuItem value="active">Active (last 30 days)</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="never">Never logged in</MenuItem>
                            <MenuItem value="subscribed_inactive">Subscribed but inactive</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={showAddonsOnly}
                              onChange={(e) => setShowAddonsOnly(e.target.checked)}
                              size="small"
                            />
                          }
                          label="With Add-Ons Only"
                          sx={{ ml: 1 }}
                        />
                        
                        <Typography variant="body2" color="text.secondary">
                          {filteredSubscriptionUsers.length} of {subscriptionUsers.length} users
                        </Typography>
                      </Box>

                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sortDirection={subscriptionSortBy === 'user' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('user', 'User')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'status' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('status', 'Status')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'plan' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('plan', 'Plan')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'expires' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('expires', 'Expires')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'trial_expires' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('trial_expires', 'Trial Expires')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'addons' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('addons', 'Add-Ons')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'last_login' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('last_login', 'Last Login')}
                              </TableCell>
                              <TableCell sortDirection={subscriptionSortBy === 'activity' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('activity', 'App Activity')}
                              </TableCell>
                              <TableCell align="center" sortDirection={subscriptionSortBy === 'logins_30d' ? subscriptionSortDirection : false}>
                                {renderSubscriptionSortLabel('logins_30d', 'Logins (30d)', 'center')}
                              </TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSubscriptionUsers
                              .slice((subscriptionPage - 1) * subscriptionRowsPerPage, subscriptionPage * subscriptionRowsPerPage)
                              .map((user) => (
                              <TableRow
                                key={user.id}
                                sx={isSubscribedButInactive(user) ? { bgcolor: 'rgba(255, 152, 0, 0.08)' } : undefined}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24 }}>
                                      {user.username.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2">
                                        {user.display_name || user.username}
                                      </Typography>
                                      {isSubscribedButInactive(user) && (
                                        <Typography variant="caption" color="warning.main">
                                          Subscribed but not using app
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={user.subscription?.status || 'No subscription'}
                                    color={
                                      user.subscription?.status === 'active' ? 'success' :
                                      user.subscription?.status === 'trial' ? 'info' :
                                      user.subscription?.status === 'expired' ? 'error' : 
                                      user.subscription?.status === 'cancelled' ? 'default' : 'warning'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {user.subscription?.plan_name || (user.subscription?.status ? 'No plan assigned' : '-')}
                                </TableCell>
                                <TableCell>
                                  {user.subscription?.status === 'active' ? 
                                    formatSubscriptionDate(user.subscription?.subscription_end_date) : '-'}
                                </TableCell>
                                <TableCell>
                                  {user.subscription?.status === 'trial' ? 
                                    formatSubscriptionDate(user.subscription?.trial_end_date) : '-'}
                                </TableCell>
                                <TableCell>
                                  {user.addons && user.addons.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      {user.addons.map((addon) => {
                                        const isTrial = addon.status === 'trial' || addon.is_trial;
                                        const daysRemaining = addon.days_remaining;
                                        const addonName = addon.addon?.name || 'Unknown';
                                        return (
                                          <Chip
                                            key={addon.id}
                                            icon={isTrial ? <ScheduleIcon /> : <CheckCircleIcon />}
                                            label={
                                              isTrial 
                                                ? `${addonName}${daysRemaining !== undefined && daysRemaining !== null ? ` (${daysRemaining}d)` : ''}`
                                                : addonName
                                            }
                                            color={isTrial ? 'warning' : 'success'}
                                            size="small"
                                            sx={{ fontSize: '0.7rem', height: '24px' }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {user.login_stats?.last_login ? (
                                    <Tooltip title={new Date(user.login_stats.last_login).toLocaleString()}>
                                      <Typography variant="body2">
                                        {new Date(user.login_stats.last_login).toLocaleDateString()}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">Never</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={getActivityStatusLabel(user)}
                                    color={
                                      user.login_stats?.activity_status === 'active' ? 'success' :
                                      user.login_stats?.activity_status === 'inactive' ? 'warning' :
                                      'default'
                                    }
                                    size="small"
                                    variant={user.login_stats?.activity_status === 'never' ? 'outlined' : 'filled'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {user.login_stats?.successful_logins ?? 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {user.subscription?.status === 'trial' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleExtendTrial(user.id, subscriptionSettings.trial_period_days || 14)}
                                        disabled={subscriptionActionLoading[`extend_${user.id}`]}
                                        startIcon={subscriptionActionLoading[`extend_${user.id}`] ? 
                                          <CircularProgress size={16} /> : null}
                                      >
                                        Reset Trial
                                      </Button>
                                    )}
                                    
                                    {(user.subscription?.status === 'trial' || user.subscription?.status === 'expired' || user.subscription?.status === 'cancelled') && availablePlans.length > 0 && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleActivateSubscription(user.id, availablePlans[0].id)}
                                        disabled={subscriptionActionLoading[`activate_${user.id}`]}
                                        startIcon={subscriptionActionLoading[`activate_${user.id}`] ? 
                                          <CircularProgress size={16} /> : null}
                                      >
                                        Activate
                                      </Button>
                                    )}

                                    {user.subscription?.status === 'cancelled' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                        onClick={() => handleExtendTrial(user.id, subscriptionSettings.trial_period_days || 14)}
                                        disabled={subscriptionActionLoading[`extend_${user.id}`]}
                                        startIcon={subscriptionActionLoading[`extend_${user.id}`] ? 
                                          <CircularProgress size={16} /> : null}
                                      >
                                        Start Trial
                                      </Button>
                                    )}
                                    
                                    {user.subscription?.status === 'active' && (
                                      <>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => handleCancelSubscription(user.id)}
                                          disabled={subscriptionActionLoading[`cancel_${user.id}`]}
                                          startIcon={subscriptionActionLoading[`cancel_${user.id}`] ? 
                                            <CircularProgress size={16} /> : null}
                                        >
                                          Cancel
                                        </Button>
                                        
                                        {availablePlans.length > 1 && (
                                          <Select
                                            size="small"
                                            value=""
                                            displayEmpty
                                            onChange={(e) => handleChangePlan(user.id, e.target.value)}
                                            disabled={subscriptionActionLoading[`change_${user.id}`]}
                                            sx={{ minWidth: 120 }}
                                          >
                                            <MenuItem value="" disabled>Change Plan</MenuItem>
                                            {availablePlans.filter(plan => plan.id !== user.subscription?.plan_id).map((plan) => (
                                              <MenuItem key={plan.id} value={plan.id}>
                                                {plan.name}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        )}
                                      </>
                                    )}
                                    
                                    {!user.subscription && availablePlans.length > 0 && (
                                      <>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="info"
                                          onClick={() => handleExtendTrial(user.id, subscriptionSettings.trial_period_days || 14)}
                                          disabled={subscriptionActionLoading[`extend_${user.id}`]}
                                          startIcon={subscriptionActionLoading[`extend_${user.id}`] ? 
                                            <CircularProgress size={16} /> : null}
                                        >
                                          Start Trial
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          onClick={() => handleActivateSubscription(user.id, availablePlans[0].id)}
                                          disabled={subscriptionActionLoading[`activate_${user.id}`]}
                                          startIcon={subscriptionActionLoading[`activate_${user.id}`] ? 
                                            <CircularProgress size={16} /> : null}
                                        >
                                          Create Subscription
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination Controls */}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Rows</InputLabel>
                          <Select
                            value={subscriptionRowsPerPage}
                            onChange={(e) => {
                              setSubscriptionRowsPerPage(e.target.value);
                              setSubscriptionPage(1);
                            }}
                            label="Rows"
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <Pagination
                          count={Math.ceil(filteredSubscriptionUsers.length / subscriptionRowsPerPage)}
                          page={subscriptionPage}
                          onChange={(event, newPage) => setSubscriptionPage(newPage)}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                        
                        <Typography variant="body2" color="text.secondary">
                          {filteredSubscriptionUsers.length === 0 ? '0' : 
                            `${(subscriptionPage - 1) * subscriptionRowsPerPage + 1}-${Math.min(subscriptionPage * subscriptionRowsPerPage, filteredSubscriptionUsers.length)}`
                          } of {filteredSubscriptionUsers.length}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          )}

          {/* Supervisor Assignments Panel - Admin Only */}
          {tabValue === 7 && isAdmin && (
            <SupervisorAssignments />
          )}

          {/* Teams Panel - Admin Only */}
          {tabValue === 8 && isAdmin && (
            <TeamManagement />
          )}

          {/* Email Management Panel - Admin Only */}
          {tabValue === 9 && isAdmin && (
            <EmailAdminTab />
          )}

          {/* Research History Tab */}
          {tabValue === (isAdmin ? 10 : (isSupervisor ? 10 : 0)) && (
            <ResearchStatistics />
          )}

          {/* Outreach Tab */}
          {tabValue === (isAdmin ? 11 : (isSupervisor ? 11 : 1)) && (
            <OutreachStatistics />
          )}

          {/* DNS Statistics Tab */}
          {tabValue === (isAdmin ? 12 : (isSupervisor ? 12 : 2)) && (
            <DNSStatistics />
          )}

          {/* DNS Change Tracker Tab - Admin Only */}
          {tabValue === (isAdmin ? 13 : 3) && isAdmin && (
            <DNSChangeTracker />
          )}
          
          </Box> {/* Close content area box */}
        </Box>

        {/* User Claims Dialog */}
        {renderUserClaimsDialog()}

        {/* Unclaimed Records Dialog */}
        {renderUnclaimedRecordsDialog()}

        {/* User Audit Dialog */}
        {renderUserAuditDialog()}

        {/* Record Details Dialog */}
        {renderRecordDetailsDialog()}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          message={snackbarMessage}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />

        {/* Thread Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteThread} onClose={handleDeleteThreadCancel}>
          <DialogTitle>Delete Thread</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this thread? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteThreadCancel}>Cancel</Button>
            <Button
              onClick={handleDeleteThread}
              color="error"
              disabled={threadDeleteLoading}
              startIcon={
                threadDeleteLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
          <DialogTitle>Bulk Delete Records</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedRecords.size} record(s)? This action cannot be undone.
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will permanently delete the selected records from the database.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmBulkDelete(false)} disabled={bulkOperationLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              color="error"
              disabled={bulkOperationLoading}
              startIcon={
                bulkOperationLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {bulkOperationLoading ? 'Deleting...' : `Delete ${selectedRecords.size} Record(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Unclaim Confirmation Dialog */}
        <Dialog open={confirmBulkUnclaim} onClose={() => setConfirmBulkUnclaim(false)}>
          <DialogTitle>Bulk Unclaim Records</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to unclaim {selectedRecords.size} record(s)? The records will remain in the database but will no longer be claimed by this user.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmBulkUnclaim(false)} disabled={bulkOperationLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkUnclaim}
              color="error"
              disabled={bulkOperationLoading}
              startIcon={
                bulkOperationLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <LayersClearIcon />
                )
              }
            >
              {bulkOperationLoading ? 'Unclaiming...' : `Unclaim ${selectedRecords.size} Record(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Record Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteRecord} onClose={handleDeleteRecordCancel}>
          <DialogTitle>Delete Record</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this record? This action cannot be undone.
            </Typography>
            {recordToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Institution:</strong> {recordToDelete.Instellingsnaam || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {recordToDelete.Vestigingsadres || 'N/A'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteRecordCancel}>Cancel</Button>
            <Button
              onClick={handleDeleteRecordConfirm}
              color="error"
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Form Dialog */}
        <UserForm
          open={userDialogOpen}
          onClose={() => setUserDialogOpen(false)}
          formData={userFormData}
          onChange={handleUserFormChange}
          onSubmit={handleUserFormSubmit}
          mode={userFormMode}
          errors={userFormErrors}
        />

        {/* User Delete Confirmation Dialog */}
        <Dialog
          open={confirmDeleteUserDialog}
          onClose={handleCloseDeleteUserDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete User
          </DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              You are about to permanently delete{' '}
              <strong>{userToDelete?.display_name || userToDelete?.username}</strong>
              {userToDelete?.username ? ` (@${userToDelete.username})` : ''}.
              This action cannot be undone.
            </Typography>

            {deletePreviewLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading affected data...
                </Typography>
              </Box>
            )}

            {deletePreviewError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deletePreviewError}
              </Alert>
            )}

            {deletePreview && (
              <>
                {deletePreview.block_reason && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {deletePreview.block_reason}
                  </Alert>
                )}

                {deletePreview.user?.is_admin && !deletePreview.block_reason && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This user has admin privileges. Admin access will be removed permanently.
                  </Alert>
                )}

                {deletePreview.subscription_status && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Active subscription status: {deletePreview.subscription_status}
                  </Alert>
                )}

                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                  The following data will be permanently deleted:
                </Alert>

                <List dense disablePadding>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="User account and profile" />
                  </ListItem>
                  {Object.entries(deletePreview.impact || {})
                    .filter(([key, count]) => (
                      key !== 'whitelisted_domains_updated' &&
                      !ANALYTICS_RETENTION_KEYS.has(key) &&
                      count > 0
                    ))
                    .map(([key, count]) => (
                      <ListItem key={key} sx={{ px: 0 }}>
                        <ListItemText
                          primary={USER_DELETE_IMPACT_LABELS[key] || key}
                          secondary={`${count} record${count === 1 ? '' : 's'}`}
                        />
                      </ListItem>
                    ))}
                  {Object.entries(deletePreview.impact || {}).every(
                    ([key, count]) => (
                      key === 'whitelisted_domains_updated' ||
                      ANALYTICS_RETENTION_KEYS.has(key) ||
                      count === 0
                    )
                  ) && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="No additional linked records found"
                        secondary="Only the user account itself will be removed"
                      />
                    </ListItem>
                  )}
                </List>

                {Object.entries(deletePreview.retained_for_analytics || {}).some(([, count]) => count > 0) && (
                  <>
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      The following analytics data will be kept, detached from the user account:
                    </Alert>
                    <List dense disablePadding>
                      {Object.entries(deletePreview.retained_for_analytics || {})
                        .filter(([, count]) => count > 0)
                        .map(([key, count]) => (
                          <ListItem key={key} sx={{ px: 0 }}>
                            <ListItemText
                              primary={USER_RETAINED_ANALYTICS_LABELS[key] || key}
                              secondary={`${count} record${count === 1 ? '' : 's'} retained with archived username`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </>
                )}

                {deletePreview.impact?.whitelisted_domains_updated > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {deletePreview.impact.whitelisted_domains_updated} whitelisted domain
                    {deletePreview.impact.whitelisted_domains_updated === 1 ? '' : 's'} will remain,
                    but the creator reference will be cleared.
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteUserDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmDeleteUser}
              color="error"
              variant="contained"
              disabled={
                loading ||
                deletePreviewLoading ||
                Boolean(deletePreview?.can_delete === false)
              }
            >
              Delete User
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default AdminPanel;
